//==================================================================
// Created by mrocha   March/15/2017
// ObjectUtil.js
// Utility methods for objects
//==================================================================
import constants from "Constants";
import factoryService   from 'FactoryService';
import ControlModel    from 'ControlModel';
import util            from 'util/Util';
import GridColumnModel from 'GridColumnModel';
import sequenceService from 'SequenceService';
import listService      from 'ListService';
import Immutable        from 'immutable';
import _ from 'lodash';

class ObjectUtil {

    // Clone nested objects. Note: some nested objects are models. e.g. table gridData items are each a model with an ID
    advancedCopy(originalValue) {
        let self = this;
        let value;
        if ( util.isEmpty(originalValue) ) {
            value = originalValue;
        } else if (originalValue.constructor && originalValue.constructor.name === 'klass' || originalValue.constructor.name === 'Date') {
            value = Object.assign(originalValue, {});
        } else if (originalValue instanceof Array) {
            value = originalValue.slice();
            for (let i = 0; i < originalValue.length; ++i) {
                const arrayVal = this.advancedCopy(originalValue[i]);
                value[i] = arrayVal;
            }
        } else if (originalValue instanceof GridColumnModel) {
            value = _.cloneDeep(originalValue); //Use of cloneDeep to prevent keeping references of original model
            this.assignId( value );

        } else if(originalValue instanceof Map || originalValue instanceof Immutable.Map || originalValue instanceof Immutable.OrderedMap){
            value = new Map(originalValue);
        } else if(originalValue instanceof ControlModel){
            value = originalValue;
            value.attributes =  this.advancedCopy(originalValue.attributes);
        } else if(originalValue instanceof Object){

            const subValue = Object.assign({}, originalValue);
            Object.keys(originalValue).forEach(
                 p => {
                     if ( originalValue.hasOwnProperty(p) && (originalValue[p] !== undefined) && (originalValue[p] !== null)) {
                        if (p !== 'widget'){
                            subValue[p] = self.advancedCopy(originalValue[p]);
                        }  else {
                            subValue[p] = originalValue[p];
                        }
                    }
                }
            );
            value = Object.assign({}, subValue);

        } else {
            value = originalValue;
        }
        return value;
    }

    assignId(model){
        const idAttr = 'id';
        model.id = sequenceService.nextDomId();
        model.set(idAttr, model.id, {silent: true});
    }

    advancedCopyBehaviors(v) {
        let self = this;
        const factoryKey = v.model.get('factoryKey');
        let control;
        if (factoryKey !== constants.CONNECTION_FACTORY_KEY) {
            const factoryParams = factoryService.behaviorsFactories[factoryKey];
            control = factoryParams.factory.createView(factoryKey, {'left': v.model.get('x'), 'top': v.model.get('y')});
            control.model = v.model.clone();
            let attributes = Object.assign(control.model.attributes, {});
            Object.keys(attributes).forEach(
                function (p, idx, array) {
                    if ( attributes.hasOwnProperty(p) && attributes[p] !== undefined  ) {
                        if (p !== 'widget' ){
                            control.model.attributes[p] = self.advancedCopy(attributes[p]);
                        }  else {
                            control.model.attributes[p] = attributes[p];
                        }
                    }
                }
            );
        } else {
           control = v;
           control.model = v.model.clone();
        }
        return control;
    }

    advancedCopyConnections(conn) {
        let newConn = Object.assign(conn, {});

        let sWidget = Object.assign({}, conn.srcCommand);
        sWidget.model.attributes = this.advancedCopy(conn.srcCommand.model.attributes);
        newConn.srcCommand = sWidget;

        let dWidget = Object.assign({}, conn.destCommand);
        dWidget.model.attributes = this.advancedCopy(conn.destCommand.model.attributes);
        newConn.destCommand = dWidget;

        let model = Object.assign({}, conn.model);
        model.attributes.destWidgetModel = {};
        model.attributes.srcWidgetModel = {};
        model.attributes = this.advancedCopy(conn.model.attributes);
        model.attributes.destWidgetModel = dWidget.model;
        model.attributes.srcWidgetModel = sWidget.model;
        newConn.model = model;

        return newConn;
    }

    advancedCopyWorkflows(v) {
        const factoryKey = v.model.get('factoryKey');
        let control;
        if (factoryKey !== constants.CONNECTION_FACTORY_KEY) {
            const factoryParams = factoryService.workflowFactories[factoryKey];
            control = factoryParams.factory.createWorkflowView(factoryKey, {'left': v.model.get('x'), 'top': v.model.get('y')});
            control.model = v.model.clone();
            let attributes = Object.assign({}, control.model.attributes)
            for (let p in attributes) {
                if ( attributes.hasOwnProperty(p) && attributes[p] !== undefined  ) {
                    if (p !== 'widget' ){
                        control.model.attributes[p] = this.advancedCopy(attributes[p]);
                    }  else {
                        control.model.attributes[p] = attributes[p];
                    }
                }
            }
        } else {
            control = v;
            control.model = v.model.clone();
        }
        return control;

    }


  //==================================================================
    // Visually selects a set HTML5 Canvas FabricJS objects, given a
    // canvas and a list of target canvas-resident objects (widgets).
    // Note that connections / paths are included in this selection.
    //==================================================================
    selectCanvasObjects(canvas, objectList) {
        // Deselect anything selected, since is causing weird behavior on select all
        let activeGroup = canvas.getActiveGroup();
        if (activeGroup) {
            activeGroup.destroy();
        }

        const group = new fabric.Group(objectList, {
            hasControls: false,
            lockScalingX: true,
            lockScalingY: true,
            lockRotation: true
        });

        // Following null setting necessary in older fabric.js
        canvas._activeObject = null;
        canvas.setActiveGroup(group.setCoords());

    }


    //==================================================================
    // Visually selects all user-editable HTML5 FabricJS objects on
    // the current behaviors or workflow canvas.
    // Does nothing if neither canvas is currently the edit canvas.
    //==================================================================
    selectAllCanvasObjects() {

        const keyPrefix = {'W': 'workflow_', 'B': 'command_'}[listService.mainKind];

        if (keyPrefix) {
            const
                keyProp = 'factoryKey',
                connectionArrow = 'connection_arrow',
                canvas = listService.fcanvas,
                objList = [];

            // Filter includes only editable Fabric.js objects of Yoda.
            canvas.forEachObject(obj => {
                if (obj.model && obj.model.has(keyProp)) {
                    const factoryKey = obj.model.get(keyProp);
                    if (factoryKey === connectionArrow || factoryKey.startsWith(keyPrefix)) {
                        objList.push(obj);
                    }
                }
            });

            this.selectCanvasObjects(canvas, objList);
        }
    }

    selectCanvas(activeObject){
        listService.fcanvas.deactivateAll().renderAll();
        activeObject.hasBorders = true;
        listService.fcanvas.setActiveObject(activeObject).renderAll();
    }

    getObjectPageId(object){
        let pageId = '';
        if(object.model.attributes.hasOwnProperty('pageId')){
            pageId = object.model.get('pageId');
        }else if(object.model.attributes.hasOwnProperty('pageID')){
            pageId = object.model.get('pageID')
        }
        return pageId;
    }

    checkCtrlSelection(option,controlList){
        if(option.e && (option.e.ctrlKey || option.e.shiftKey) && option.target) {
            option.target.model.set('selected', true, {silent: true})

            let currentActiveGroup = controlList.reduce((accum, control) => {
                if (util.isTrue(control.model.get('selected'))) {
                    accum.push(listService.getCanvasObjectById(control.model.get('id')))
                }
                return accum;
            }, []);
            if (currentActiveGroup.length > 1) {
                listService.fcanvas.discardActiveObject();
                listService.fcanvas.discardActiveGroup();
                this.selectCanvasObjects(listService.fcanvas, currentActiveGroup);
            }
        }
        else if(option.e && option.e.type ==='mousedown' && !(option.e.ctrlKey || option.e.ctrlKey) && option.target) {
            _.forEach(controlList,(control => {
                control.model.set('selected', false, {silent: true});
            }));
            option.target.model.set('selected', true, {silent: true})
        }
    }

}

const objectUtil = new ObjectUtil();
export default objectUtil;
